import React, { useRef } from 'react'
import {
  EffectCreative,
  Pagination,
  Keyboard
} from 'swiper'

import {
  Swiper,
  SwiperSlide
} from 'swiper/react'

import { Label } from '@/atoms/Typography'
import Card from '@/molecules/Card'

import {
  StyledSingleSlider,
  StyledSingleSliderSlide,
  StyledSingleSliderPagination,
  StyledSingleSliderPaginationIndicators,
} from './styles'

const swiperProps = {
  centeredSlides: true,
  slidesPerView: 'auto',
  effect: 'creative',
}

const SingleSlider = ({
  list,
  type,
  label,
}) => {
  const liveRegionRef = useRef(null)

  const updateSlideAccessibility = (swiperInstance) => {
    if (!swiperInstance?.slides) return

    swiperInstance.slides.forEach((slide, index) => {
      const isActive = index === swiperInstance.activeIndex

      slide.setAttribute('aria-hidden', !isActive)
      slide.setAttribute('tabindex', isActive ? '0' : '-1')

      if (!isActive) {
        // Additional VoiceOver-specific hiding
        slide.setAttribute('inert', '')
      } else {
        slide.removeAttribute('inert')
      }

      const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])')

      focusableElements.forEach(element => {
        element.setAttribute('tabindex', isActive ? '0' : '-1')
        if (!isActive) {
          element.setAttribute('aria-hidden', 'true')
        } else {
          element.removeAttribute('aria-hidden')
        }
      })
    })
  }

  const handleSlideChange = (swiperInstance) => {
    updateSlideAccessibility(swiperInstance)

    // Announce slide change to screen readers
    const currentSlide = swiperInstance.activeIndex + 1
    const totalSlides = swiperInstance.slides.length
    const currentItem = list[swiperInstance.activeIndex]

    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = `Slide ${currentSlide} di ${totalSlides}: ${currentItem?.title || 'Contenuto'}`
    }
  }

  return (
    list &&
    !!list.length &&
    <StyledSingleSlider>
      {/* Live region for screen reader announcements */}
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      />

      <StyledSingleSliderPagination>
        <Label typo="labelBig">{label}</Label>
        <StyledSingleSliderPaginationIndicators className='single-slider-custom-pagination' />
      </StyledSingleSliderPagination>
      <Swiper
        {...swiperProps}
        modules={[EffectCreative, Pagination, Keyboard]}
        onSwiper={(swiperInstance) => {
          setTimeout(() => updateSlideAccessibility(swiperInstance), 0)
        }}
        onSlideChange={handleSlideChange}
        keyboard={{
          enabled: true,
          onlyInViewport: true,
        }}
        role="region"
        aria-label={`Slider ${label || 'contenuti'}`}
        aria-roledescription="slider"
        aria-live="polite"
        pagination={{
          type: 'custom',
          el: '.single-slider-custom-pagination',
          renderCustom: (_, current, total) => {
            return `
              <span>${current}</span>
              <span>${total}</span>
            `
          }
        }}
        creativeEffect={{
          progressMultiplier: 1,
          limitProgress: 2,
          prev: {
            translate: ['-100%', 0, 0],
            scale: 0.75,
            origin: 'center right',
          },
          next: {
            translate: ['100%', 0, 0],
            scale: 0.75,
            origin: 'center left',
          },
        }}
        breakpoints={{
          768: {
            longSwipes: false,
            followFinger: true,

            creativeEffect: {
              progressMultiplier: 1,
              limitProgress: 2,
              prev: {
                translate: ['-100%', 0, 0],
                scale: 0.75,
                // origin: 'center right',
              },
              next: {
                translate: ['100%', 0, 0],
                scale: 0.75,
                // origin: 'center left',
              },
            }
          }
        }}
      >
        {
          list.map((item, i) => (
            <SwiperSlide
              key={i}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} di ${list.length}: ${item.title || 'Contenuto'}`}
            >
              <StyledSingleSliderSlide>
                <Card
                  type={type}
                  item={{
                    ...item,
                    hoverIcon: {
                      big: true,
                      visible: false }
                  }}
                />
              </StyledSingleSliderSlide>
            </SwiperSlide>
          ))
        }
      </Swiper>
    </StyledSingleSlider>
  )
}

export default SingleSlider
